import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useReactFlow, XYPosition } from 'reactflow';
import { InstanceMapContext } from '../canvas-view/canvas-flow';
import useLayerHierarchy from '../../hooks/useLayerHierarchy';
import { Instance, InstanceMap, InstanceState } from '../../triggers/semantic-dive/semantic-dive';
import { styleNodesAndEdges } from '../../triggers/semantic-dive/semantic-dive.helper';

import Xarrow, { useXarrow, Xwrapper } from "react-xarrows";
import './layer-view.scss';
import Layer from './layer/layer';
import { TopicNodeData, TypeTopicNode } from '../../nodes/topic-node/topic-node.model';
import { createTopicNodeToReturn } from '../../nodes/topic-node/topic-node.helper';
import { duplicateNode } from '../../nodes/node.helper';
import { getInstanceSubtree } from './layer-view.helper';

const LayerView = (props: any) => {
  const { instanceMap, setInstanceMap } = useContext(InstanceMapContext);
  const [hasLoadedLayers, setHasLoadedLayers] = useState(false);
  const [hasUpdatedLayers, setHasUpdatedLayers] = useState(false);
  const [madeChange, setMadeChange] = useState(false);
  const [recommendedSubtopics, setRecommendedSubtopics] = useState<{ [x: string]: string[]; }>({[props.currentTopicId]: [] as string[]});

  const instances: Instance[] = useLayerHierarchy(Object.values(instanceMap))

  const viewportData = {
    minLeft: Math.min(...instances.map(instance => instance.position.x)),
    maxLeft: Math.max(...instances.map(instance => instance.position.x)),
    top: Math.max(...instances.map(instance => instance.position.y)),
    maxLevel: Math.max(...instances.map(instance => instance.level))
  }

  // Robust to rerenders, sets to true once "Enter Hierarchy View animation" completes
  // After 0.5 seconds, the view will have "loaded the layers"
  const memoLoadedLayers = useRef(false);
  const memoizedValue = useMemo(() => {
    memoLoadedLayers.current = hasLoadedLayers;
    return hasLoadedLayers;
  }, [hasLoadedLayers]);

  useEffect(() => {
    // Triggering an update to layers to allow the arrows to reposition themselves
    setHasUpdatedLayers(false);
    setTimeout(() => {
      setHasUpdatedLayers(true);
    }, 200);
  }, [madeChange]);

  /**
   * Allows edges to be rendered after layers have
   * Scrolls the window to the position of the layer
   */
  useEffect(() => {
    const currInstance = instanceMap[props.currentTopicId];
    setTimeout(() => {
      setHasUpdatedLayers(true);
      setHasLoadedLayers(true);
    }, 500);
    window.scrollBy(viewportData.maxLeft+125, 0);
    window.scrollTo({
      top: 380,
      left: viewportData.maxLeft+50,
      behavior: 'smooth'
    });
  }, []);

  const isLayerRoot = (instance: Instance) => {
    return instance.topicNode.id === 'topic-root';
  }

  const randomPosition = () => {
    return {
      x: window.innerWidth/2 + Math.floor(Math.random()*800)-400,
      y: window.innerHeight/2 + Math.floor(Math.random()*800)-400
    };
  }

  /**
   * Creates a new layer to build a new hierarchy for
   * isHigherLevel if the layer to add is to add above all layers
   */
  const addNewLayer = useCallback((isHigherLevel?: boolean, parentTopicId?: string, topicName?: string) => {

    const newTopic: TypeTopicNode = createTopicNodeToReturn(topicName ?? 'New Topic', '',
    {
      x: window.innerWidth/2,
      y: window.innerHeight/2,
    }, [], false);

    newTopic.data.instanceState = InstanceState.CURRENT;
    console.log(newTopic.data.state.topic);

    const parentInstance = instanceMap[parentTopicId ?? ''];

    // First, assume values of new instance is to be a new parent layer
    const newInstance: Instance = {
      parentId: '',
      topicNode: newTopic,
      jsonObject: {
        nodes: [newTopic],
        edges: [],
        viewport: { x: 0, y: 0, zoom: 1 }
      },
      level: 0,
      canvasImageURL: '',
      position: { x: 0, y: 0 },
    }

    // If new layer is higher level, shift all layers down one level
    if (isHigherLevel) {
      // To duplicate parent topics and store them in parent layer
      const parentTopics: TypeTopicNode[] = [];

      Object.keys(instanceMap).forEach(topicId => {
        // Parent topics are now the children of this topic
        if (!instanceMap[topicId].parentId || instanceMap[topicId].parentId === 'topic-root') {
          instanceMap[topicId].parentId = newTopic.id;
          parentTopics.push(instanceMap[topicId].topicNode);
        }
        instanceMap[topicId].level++;
      });

      // Add all parent topic nodes to parent layer
      newInstance.jsonObject.nodes
        .push(...parentTopics.map(topic => {
          const duplicate: TypeTopicNode = duplicateNode(topic);
          duplicate.position = randomPosition();
          duplicate.data.originalId = topic.id;
          duplicate.data.instanceState = InstanceState.WAS;
          duplicate.hidden = false;
          return duplicate;
        }));
    } else if (parentTopicId && parentInstance) {  // If new layer is a sublayer
      // console.log('adding sublayer, parent Instance', parentInstance.topicNode.data.state.topic);
      newInstance.parentId = parentInstance.topicNode.id;
      newInstance.level = parentInstance.level + 1;

      const duplicate: TypeTopicNode = duplicateNode(newTopic);
      // Shifting new node to potentially empty space in the parent layer
      duplicate.position = randomPosition();
      duplicate.data.originalId = newTopic.id;
      duplicate.data.instanceState = InstanceState.WAS;
      instanceMap[newInstance.parentId].jsonObject.nodes.push(duplicate);
    }

    setInstanceMap(Object.assign(instanceMap, {
      [newTopic.id]: newInstance
    }));
    setMadeChange(!madeChange);
    // console.log('added new layer', instanceMap, newInstance.topicNode.data.state.topic);
  }, [instanceMap, madeChange]);

  /**
   * Deletes the layer and the entire layer subtree
   */
  const deleteLayer = useCallback((instanceId: string) => {
    // if (Object.values(Insta))
    const instanceSubtree = getInstanceSubtree(instanceId, instanceMap);
    instanceSubtree.forEach(instance => {
      delete instanceMap[instance.topicNode.id];
    });
    delete instanceMap[instanceId];
    setInstanceMap(instanceMap);
    setMadeChange(!madeChange);
  }, [instanceMap, madeChange]);

  return (
    <div
      id="layer-view"
      style={{
        overflow: 'clip',
        width: `calc(100vw + ${viewportData.maxLeft-viewportData.minLeft}px)`,
        height: `calc(100vh + ${viewportData.top}px)`,
      }}
    >
      {/* Add Broad Topic Layer */}
      <div className='add-layer broad'
        onClick={() => addNewLayer(true)}
      >
        <a>Broad Topic</a>
      </div>

      {/* Add New Layer Hierarchy */}
      <div className='add-layer side'
        onClick={() => addNewLayer()}
        style={{
          left: `calc(50% + ${viewportData.maxLeft-50}px)`,
        }}
      >
        <a>New Hierarchy</a>
      </div>

{/* change css for on hover thing on add lauer */}
      <Xwrapper>
        <div className='layer-stacks' id='layer-stacks'>
            { instances.map(instance => {
              if (!isLayerRoot(instance)) {
                return (<>
                  { <Layer
                      hasLoadedLayers={memoizedValue}
                      layerTopicId={instance.topicNode.id}
                      layerPosition={instance.position}
                      currentTopicId={props.currentTopicId}
                      viewportData={viewportData}
                      hideLayerView={props.hideLayerView}
                      addNewLayer={addNewLayer}
                      deleteLayer={deleteLayer}
                      recommendedSubtopics={recommendedSubtopics}
                      setRecommendedSubtopics={setRecommendedSubtopics}
                   /> }
                  { hasUpdatedLayers && instance.parentId !== 'topic-root' ?
                    <Xarrow
                      key={`layer-arrow-${instance.topicNode.id}`}
                      divContainerProps={{
                        className: 'fade-in'
                      }}
                      start={`layer-${instance.parentId}`}
                      end={`layer-${instance.topicNode.id}`}
                      startAnchor='middle'
                      endAnchor='middle'
                      path='smooth'
                      color='rgba(199, 204, 214, 0.765)'
                      zIndex={viewportData.maxLevel-instance.level+1}
                      headSize={4}
                      curveness={0.5}
                    /> : <></>
                  }
                </>)
              } else {
                return <></>
              }
            }).reverse() }
        </div>
      </Xwrapper>
    </div>
  )
}

export default LayerView;


// TEMP CODE: To animate below layers to rise up and above layers to land down
// paste code when layer onLoad={() => {
// }}
// Animate the other layers entering the view
// if (instance.topicNode.id !== currInstance.topicNode.id) {
//   const layerInstance = instanceMap[instance.topicNode.id];
//   const enterDelay = Math.abs(currInstance.level-layerInstance.level)*100;
//   console.log(event);
//   const thisElement = event.target.parentElement as HTMLElement;
//   if (layerInstance.level <= currInstance.level) {
//     // Layer lands
//     setTimeout(() => {
//       thisElement.classList.add('above-layer');
//     }, enterDelay);
//     // Remove this animation class after animation over
//     setTimeout(() => {
//       thisElement.classList.remove('above-layer');
//     }, 500 + enterDelay);
//   } else {
//     // Layer rises
//     setTimeout(() => {
//       thisElement.classList.add('below-layer');
//     }, enterDelay);
//     // Remove this animation class after animation over
//     setTimeout(() => {
//       thisElement.classList.remove('below-layer');
//     }, 500 + enterDelay);
//   }
// }